/**
 * Offline Storage Service
 * Handles local storage, IndexedDB, and offline data management
 */

import { WorkoutSession, Exercise, UserProfile, WorkoutLog } from '@/types/workout';

export interface OfflineWorkoutData {
  id: string;
  userId: string;
  workoutData: WorkoutLog;
  timestamp: Date;
  synced: boolean;
  retryCount: number;
}

export interface OfflineUserData {
  profile: UserProfile;
  lastSync: Date;
  version: number;
}

export interface SyncQueueItem {
  id: string;
  type: 'workout' | 'profile' | 'progress';
  data: any;
  timestamp: Date;
  retryCount: number;
  maxRetries: number;
}

class OfflineStorageService {
  private dbName = 'HyroxOfflineDB';
  private dbVersion = 1;
  private db: IDBDatabase | null = null;

  async initialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        reject(new Error('Failed to open IndexedDB'));
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Offline workouts store
        if (!db.objectStoreNames.contains('offlineWorkouts')) {
          const workoutStore = db.createObjectStore('offlineWorkouts', { keyPath: 'id' });
          workoutStore.createIndex('userId', 'userId', { unique: false });
          workoutStore.createIndex('synced', 'synced', { unique: false });
          workoutStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        // User data store
        if (!db.objectStoreNames.contains('userData')) {
          db.createObjectStore('userData', { keyPath: 'userId' });
        }

        // Sync queue store
        if (!db.objectStoreNames.contains('syncQueue')) {
          const syncStore = db.createObjectStore('syncQueue', { keyPath: 'id' });
          syncStore.createIndex('type', 'type', { unique: false });
          syncStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        // Cached exercises store
        if (!db.objectStoreNames.contains('cachedExercises')) {
          db.createObjectStore('cachedExercises', { keyPath: 'id' });
        }

        // Cached workout templates store
        if (!db.objectStoreNames.contains('cachedTemplates')) {
          const templateStore = db.createObjectStore('cachedTemplates', { keyPath: 'id' });
          templateStore.createIndex('userId', 'userId', { unique: false });
        }
      };
    });
  }

  // Workout Data Management
  async saveOfflineWorkout(workoutData: OfflineWorkoutData): Promise<void> {
    if (!this.db) await this.initialize();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['offlineWorkouts'], 'readwrite');
      const store = transaction.objectStore('offlineWorkouts');
      
      const request = store.put(workoutData);
      
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to save offline workout'));
    });
  }

  async getOfflineWorkouts(userId: string): Promise<OfflineWorkoutData[]> {
    if (!this.db) await this.initialize();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['offlineWorkouts'], 'readonly');
      const store = transaction.objectStore('offlineWorkouts');
      const index = store.index('userId');
      
      const request = index.getAll(userId);
      
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(new Error('Failed to get offline workouts'));
    });
  }

  async getUnsyncedWorkouts(userId: string): Promise<OfflineWorkoutData[]> {
    if (!this.db) await this.initialize();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['offlineWorkouts'], 'readonly');
      const store = transaction.objectStore('offlineWorkouts');
      
      const request = store.getAll();
      
      request.onsuccess = () => {
        const allWorkouts = request.result || [];
        const unsyncedWorkouts = allWorkouts.filter(
          workout => workout.userId === userId && !workout.synced
        );
        resolve(unsyncedWorkouts);
      };
      request.onerror = () => reject(new Error('Failed to get unsynced workouts'));
    });
  }

  async markWorkoutSynced(workoutId: string): Promise<void> {
    if (!this.db) await this.initialize();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['offlineWorkouts'], 'readwrite');
      const store = transaction.objectStore('offlineWorkouts');
      
      const getRequest = store.get(workoutId);
      
      getRequest.onsuccess = () => {
        const workout = getRequest.result;
        if (workout) {
          workout.synced = true;
          const putRequest = store.put(workout);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(new Error('Failed to mark workout as synced'));
        } else {
          resolve(); // Workout not found, consider it synced
        }
      };
      getRequest.onerror = () => reject(new Error('Failed to get workout for sync update'));
    });
  }

  // User Data Management
  async saveUserData(userId: string, userData: OfflineUserData): Promise<void> {
    if (!this.db) await this.initialize();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['userData'], 'readwrite');
      const store = transaction.objectStore('userData');
      
      const request = store.put({ userId, ...userData });
      
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to save user data'));
    });
  }

  async getUserData(userId: string): Promise<OfflineUserData | null> {
    if (!this.db) await this.initialize();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['userData'], 'readonly');
      const store = transaction.objectStore('userData');
      
      const request = store.get(userId);
      
      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          const { userId: _, ...userData } = result;
          resolve(userData as OfflineUserData);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(new Error('Failed to get user data'));
    });
  }

  // Sync Queue Management
  async addToSyncQueue(item: SyncQueueItem): Promise<void> {
    if (!this.db) await this.initialize();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['syncQueue'], 'readwrite');
      const store = transaction.objectStore('syncQueue');
      
      const request = store.put(item);
      
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to add item to sync queue'));
    });
  }

  async getSyncQueue(): Promise<SyncQueueItem[]> {
    if (!this.db) await this.initialize();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['syncQueue'], 'readonly');
      const store = transaction.objectStore('syncQueue');
      
      const request = store.getAll();
      
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(new Error('Failed to get sync queue'));
    });
  }

  async removeFromSyncQueue(itemId: string): Promise<void> {
    if (!this.db) await this.initialize();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['syncQueue'], 'readwrite');
      const store = transaction.objectStore('syncQueue');
      
      const request = store.delete(itemId);
      
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to remove item from sync queue'));
    });
  }

  // Cache Management
  async cacheExercises(exercises: Exercise[]): Promise<void> {
    if (!this.db) await this.initialize();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['cachedExercises'], 'readwrite');
      const store = transaction.objectStore('cachedExercises');
      
      let completed = 0;
      const total = exercises.length;
      
      if (total === 0) {
        resolve();
        return;
      }
      
      exercises.forEach(exercise => {
        const request = store.put(exercise);
        request.onsuccess = () => {
          completed++;
          if (completed === total) resolve();
        };
        request.onerror = () => reject(new Error('Failed to cache exercises'));
      });
    });
  }

  async getCachedExercises(): Promise<Exercise[]> {
    if (!this.db) await this.initialize();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['cachedExercises'], 'readonly');
      const store = transaction.objectStore('cachedExercises');
      
      const request = store.getAll();
      
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(new Error('Failed to get cached exercises'));
    });
  }

  // Utility Methods
  async clearAllData(): Promise<void> {
    if (!this.db) await this.initialize();

    const stores = ['offlineWorkouts', 'userData', 'syncQueue', 'cachedExercises', 'cachedTemplates'];
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(stores, 'readwrite');
      
      let completed = 0;
      const total = stores.length;
      
      stores.forEach(storeName => {
        const store = transaction.objectStore(storeName);
        const request = store.clear();
        
        request.onsuccess = () => {
          completed++;
          if (completed === total) resolve();
        };
        request.onerror = () => reject(new Error(`Failed to clear ${storeName}`));
      });
    });
  }

  async getStorageUsage(): Promise<{ used: number; quota: number }> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      return {
        used: estimate.usage || 0,
        quota: estimate.quota || 0
      };
    }
    return { used: 0, quota: 0 };
  }

  // Connection Status
  isOnline(): boolean {
    return navigator.onLine;
  }

  onConnectionChange(callback: (isOnline: boolean) => void): () => void {
    const handleOnline = () => callback(true);
    const handleOffline = () => callback(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Return cleanup function
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }
}

export const offlineStorageService = new OfflineStorageService();
